import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { pipelineApi, aiApi } from '../../services/api';
import { formatAgency } from '../../utils/agency';
import { sourceSiteName } from '../../utils/source';
import ErrorState from '../../components/ui/ErrorState';
import EmptyState from '../../components/ui/EmptyState';
import { SkeletonBlock } from '../../components/ui/Skeleton';
import AnalysisResult, { AnalysisPayload, isAnalysisPayload } from '../../components/AnalysisResult';
import BidSchedulePanel from '../../components/proposal/BidSchedulePanel';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  SparklesIcon,
  XCircleIcon,
} from 'components/icons';

/**
 * The pursuit workspace: one page per pipeline row. Before this, the work on
 * one solicitation was spread over six pages (opportunity detail, documents,
 * proposal writer, agents, pipeline card, chat) and the user carried the
 * solicitation number between them. Everything here comes from one read,
 * GET /pipeline/workspace/:key; deep editing still happens on the pages that
 * own it, which this page links to with the right id already filled in.
 */

type Tab = 'overview' | 'documents' | 'requirements' | 'pricing' | 'proposal' | 'activity';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'documents', label: 'Documents' },
  { id: 'requirements', label: 'Requirements' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'proposal', label: 'Proposal' },
  { id: 'activity', label: 'Activity' },
];

// The six stages a pursuit normally walks through. Lost and archived are exits,
// offered as a separate action rather than steps on the path.
const STAGE_PATH = ['new', 'analyzing', 'qualified', 'proposal', 'submitted', 'awarded'] as const;
const STAGE_LABEL: Record<string, string> = {
  new: 'New', analyzing: 'Analyzing', qualified: 'Qualified', proposal: 'Proposal',
  submitted: 'Submitted', awarded: 'Awarded', lost: 'Lost', archived: 'Archived',
};
const AGENT_LABEL: Record<string, string> = {
  'contract-scout': 'Analyze',
  'analyst-agent': 'Consultant',
  'compliance-agent': 'Compliance check',
  'vendor-matcher': 'Teaming search',
  'email-agent': 'Draft response',
  'proposal-writer': 'Proposal draft',
  'proposal-reviewer': 'Proposal review',
  'market-analyst': 'Market analysis',
};
const KIND_LABEL: Record<string, string> = {
  site_visit: 'Site visit', conference: 'Pre-proposal conference', rsvp: 'RSVP', base_access: 'Base access',
  question_cutoff: 'Questions due', registration: 'Registration',
};
const CATEGORY_LABEL: Record<string, string> = {
  eligibility: 'Eligibility', compliance: 'Compliance', technical: 'Technical', pricing: 'Pricing',
  submission: 'Submission', documentation: 'Documentation', personnel: 'Personnel', vendor_sourcing: 'Vendor sourcing',
  evaluation_factor: 'Evaluation factor', sow_requirement: 'Statement of work', compliance_item: 'Compliance',
  form: 'Form', certification: 'Certification', page_limit: 'Page limit', format: 'Format',
};

interface Workspace {
  pursuit: {
    id: string; stage: string; matchScore: number; notes: string | null; classification: string | null;
    createdAt: string; stageHistory: Array<{ from: string; to: string; at: string }>; lastOpenedAt: string | null;
    amendmentReviewedAt: string | null;
    submission: { method: string; recipient: string | null; sentAt: string; confirmation: string | null; recordedAt: string; gate: { blockers: string[]; forced: boolean } } | null;
    outcome: {
      kind: 'won' | 'lost' | 'missed'; at: string; note: string | null; awardee: string | null; amount: string | null; awardDate: string | null;
      askBasis: { rule: string; title: string; request: string; clock: string; evidence: string[] } | null;
      debriefAt?: string | null;
      clocks?: Array<{ key: string; label: string; date: string; basis: string }>;
    } | null;
  };
  opportunity: {
    id: string; samNoticeId: string; title: string; agency: string | null; solicitationNumber: string | null;
    type: string | null; setAside: string | null; setAsideDescription: string | null; naics: string | null;
    psc: string | null; postedDate: string | null; responseDeadline: string | null; originalDeadline: string | null;
    /** False when SAM.gov gave a bare date; the stored time is a placeholder. */
    deadlineTimeKnown: boolean;
    placeOfPerformance: any; popState: string | null; uiLink: string | null; summary: string | null;
    pointOfContact: any;
    /** 'sam' for federal notices; 'fl_vbs' or 'al_rfp' for state rows. */
    source?: string | null;
  };
  keyDates: {
    docsTotal: number; docsRead: number; computedAt: string;
    actions: Array<{ key: string; kind: string; requirement: string; deadline: string | null; deadlineSource: string; source: string; quote: string; status: 'past' | 'today' | 'upcoming' | 'undated' | 'closed'; daysRemaining: number | null; done: boolean; doneAt: string | null }>;
    conflicts: Array<{ filename: string; stated_in_document: string; deadline_on_record: string; days_apart: number; quote: string; kind: 'conflict' | 'superseded' }>;
    amendments: {
      count: number; acknowledgmentRequired: boolean | null; designations: string[]; unreadable: number;
      items: Array<{ filename: string; designation: string | null; acknowledgmentRequired: boolean | null; textUnreadable: boolean; changes: Array<{ field?: string; component?: 'date' | 'time'; movedFrom?: string; movedTo?: string; text: string }> }>;
    };
    nextDue: { key: string; kind: string; requirement: string; deadline: string | null; daysRemaining: number | null } | null;
  } | null;
  analysis: {
    taskId: string; jobId: string; createdAt: string; completedAt: string | null;
    result: Record<string, unknown> | null;
    keyTermEvidence: Record<string, { evidence: string; source?: string; found?: string }> | null;
    docsRead: number;
  } | null;
  /** The Award Notice for this solicitation number, when SAM.gov has posted one. */
  award: { samNoticeId: string; postedDate: string | null; awardee: string | null; amount: string | null; date: string | null; number: string | null } | null;
  notices: Array<{
    id: string; samNoticeId: string; title: string; type: string | null; postedDate: string | null;
    responseDeadline: string | null; superseded: boolean; current: boolean; newSinceLastOpen: boolean;
  }>;
  eligibility: {
    canBid: boolean; fatalCount: number; warningCount: number;
    issues: Array<{ code: string; severity: string; category: string; title: string; detail: string; actionHint?: string }>;
  };
  verdict: {
    kind: 'eligible' | 'blocked' | 'overridden' | 'not_a_solicitation' | 'sole_source' | 'already_awarded';
    headline: string; respondDifferently: string | null; competition: string; gatesApply: boolean; canBid: boolean | null;
    fatalCount: number; warningCount: number;
    issues: Array<{ code: string; severity: string; weight: 'fatal' | 'warning' | 'informational'; category: string; title: string; detail: string; actionHint?: string; actionPath: string | null; actionLabel: string | null }>;
    assessedNotice: { samNoticeId: string; postedDate: string | null } | null;
    changedSinceAdded: Array<{ field: 'setAside' | 'naics' | 'responseDeadline' | 'place'; from: string | null; to: string | null }>;
    override: { at: string; note: string | null } | null;
    /** Which registration regime the checks ran under. */
    jurisdiction?: { level: 'federal' | 'state'; state?: string; stateName?: string; system?: string };
  };
  documents: {
    total: number; ready: number; readable: number; failed: number;
    items: Array<{ id: string; filename: string; size: number; url: string; status: string; extractionStatus: string | null; extractionError: string | null; createdAt: string; sourceId: string | null; readableChars: number | null; readability: 'ok' | 'hollow' | 'undecodable' | 'placeholder' | null }>;
  };
  proposal: {
    id: string; title: string; status: string; updatedAt: string;
    sections: Array<{ id: string; title: string; sectionType: string; wordCount: number; version: number; updatedAt: string }>;
    checklist: Array<{ id: string; title: string; category: string; isCompleted: boolean; isRequired: boolean; linkedSection: string | null; source: string; inputLabel: string | null }>;
    checklistProgress: { total: number; completed: number };
    matrix: Array<{ id: string; requirementText: string; sourceRef: string | null; category: string; targetSection: string | null; status: string }>;
    matrixStats: { total: number; addressed: number; partial: number; missing: number; na: number; coveragePct: number } | null;
  } | null;
  nextStep: {
    key: string; title: string; detail: string | null; blocking: boolean;
    action: { type: 'none' } | { type: 'fetch_documents' } | { type: 'analyze' } | { type: 'open_notice'; samNoticeId: string } | { type: 'open_award'; samNoticeId: string }
      | { type: 'open_tab'; tab: 'documents' | 'requirements' | 'proposal' } | { type: 'open_proposal' } | { type: 'fix_eligibility' } | { type: 'submit'; email: string | null } | { type: 'tick_key_date'; key: string } | { type: 'archive' };
  };
  activity: Array<{ id: string; jobId: string; agentType: string; status: string; error: string | null; createdAt: string; completedAt: string | null }>;
  conversations: Array<{ id: string; title: string; updatedAt: string; messages: number }>;
}

const fmtDate = (d?: string | null, withTime = false) => {
  if (!d) return '';
  const date = new Date(d);
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric',
    ...(date.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
    ...(withTime ? { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' } : {}),
  });
};

/**
 * A deadline SAM.gov gave as a bare date is stored at UTC midnight; shown in a
 * US zone that is the evening before. Render those as the date they are.
 */
const fmtDeadline = (d?: string | null, timeKnown = true) => {
  if (!d) return '';
  if (timeKnown) return fmtDate(d, true);
  const date = new Date(d);
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
    ...(date.getUTCFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
  });
};

/** Whole days from today (UTC) to a YYYY-MM-DD date. */
const daysUntilDate = (iso: string) => {
  const d = new Date(iso + 'T00:00:00Z');
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - today) / 86400000);
};

const fmtMoney = (v?: string | number | null) => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (n == null || Number.isNaN(n)) return '';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: n % 1 ? 2 : 0 });
};

const fmtSize = (n: number) => {
  if (!n) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/** Days until a deadline, with the tone the number deserves. */
function dueIn(deadline?: string | null, timeKnown = true, site = 'SAM.gov') {
  if (!deadline) return { big: 'No date', small: `${site} lists no response date`, tone: 'text-content-muted' };
  const at = new Date(deadline);
  // A bare date means "that day"; count to the end of it, not to midnight UTC.
  const endMs = timeKnown ? at.getTime() : Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate() + 1) - 1;
  const ms = endMs - Date.now();
  const days = Math.ceil(ms / 86400000);
  const when = timeKnown ? fmtDate(deadline, true) : `${fmtDeadline(deadline, false)} · time not stated on ${site}`;
  if (ms < 0) return { big: 'Closed', small: `Was due ${when}`, tone: 'text-content-muted' };
  if (days === 0) return { big: 'Today', small: when, tone: 'text-danger' };
  if (days === 1) return { big: '1 day', small: when, tone: 'text-danger' };
  if (days <= 7) return { big: `${days} days`, small: when, tone: 'text-warning' };
  return { big: `${days} days`, small: when, tone: 'text-content' };
}

function placeLabel(pop: any, popState: string | null) {
  if (!pop && !popState) return '';
  const city = pop?.city?.name && pop.city.name !== '0' ? pop.city.name : '';
  const state = pop?.state?.code || popState || '';
  const zip = pop?.zip || '';
  return [city, state, zip].filter(Boolean).join(city && state ? ', ' : ' ').replace(', ' + zip, ' ' + zip);
}

interface AwardRow { awardId: string; recipient: string; amount: number; startDate: string | null; endDate: string | null; description: string; sameOffice: boolean; active: boolean; scope: 'state' | 'national'; url: string }
interface AwardHistory {
  query: { toptier: string | null; subtier: string | null; naics: string | null; psc: string | null; state: string | null; officeCode: string | null; since: string };
  resolved: boolean; rows: AwardRow[];
  summary: { currentHolder: AwardRow | null; sameOffice: number; active: number; total: number; obligated: number };
  fetchedAt: string;
}
const money = (n: number) => n >= 1e6 ? `$${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M` : n >= 1e3 ? `$${Math.round(n / 1e3)}K` : `$${Math.round(n).toLocaleString()}`;
const monthYear = (d?: string | null) => d ? new Date(d + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }) : '';
const titleCaseName = (s: string) => s.toLowerCase().replace(/(^|[\s(\-\/])([a-z])/g, (m, pre, c) => pre + c.toUpperCase()).replace(/\b(Llc|Inc|Ltd|Lp|Pc|Jv)\b\.?/g, (m) => m.toUpperCase());

const Pill: React.FC<{ tone?: 'accent' | 'success' | 'warning' | 'danger' | 'muted'; children: React.ReactNode }> = ({ tone = 'muted', children }) => {
  const cls = {
    accent: 'bg-accent-sunk text-accent', success: 'bg-success-sunk text-success', warning: 'bg-warning-sunk text-warning',
    danger: 'bg-danger-sunk text-danger', muted: 'bg-surface-sunk text-content-muted',
  }[tone];
  return <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-caption font-semibold ${cls}`}>{children}</span>;
};

const Card: React.FC<{ className?: string; id?: string; children: React.ReactNode }> = ({ className = '', id, children }) => (
  <div id={id} className={`rounded-token-md border border-border bg-surface shadow-e1 ${className}`}>{children}</div>
);

const Overline: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`text-overline uppercase text-content-muted ${className}`}>{children}</div>
);

const PursuitWorkspace: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (TABS.some((t) => t.id === searchParams.get('tab')) ? searchParams.get('tab') : 'overview') as Tab;
  const setTab = (t: Tab) => setSearchParams(t === 'overview' ? {} : { tab: t }, { replace: true });

  const [ws, setWs] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showAllMatrix, setShowAllMatrix] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!key) return;
    if (!silent) setLoading(true);
    try {
      // A real open counts as a visit; the extraction poll and refreshes do not.
      const r = await pipelineApi.getWorkspace(key, { touch: !silent });
      setWs(r.data.data);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.status === 404 ? 'not_found' : (e?.response?.data?.error?.message || 'Could not load this pursuit'));
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => { load(); }, [load]);

  // While attachments are extracting, refresh until they settle.
  useEffect(() => {
    if (!ws || ws.documents.total === 0 || ws.documents.ready + ws.documents.failed >= ws.documents.total) return;
    const t = setTimeout(() => load(true), 6000);
    return () => clearTimeout(t);
  }, [ws, load]);

  // While an analysis is running, refresh every ten seconds for up to ten minutes.
  const [analysisWaitStart, setAnalysisWaitStart] = useState<number | null>(null);
  const [, forceTick] = useState(0);
  const analysisRunningNow = !!ws && ws.activity.some((a) => a.agentType === 'contract-scout' && (a.status === 'processing' || a.status === 'queued'));
  useEffect(() => {
    if (!analysisRunningNow) { setAnalysisWaitStart(null); return; }
    setAnalysisWaitStart((s) => s ?? Date.now());
    const t = setInterval(() => {
      forceTick((n) => n + 1);
      load(true);
    }, 10000);
    const stop = setTimeout(() => clearInterval(t), 10 * 60 * 1000);
    return () => { clearInterval(t); clearTimeout(stop); };
  }, [analysisRunningNow, load]);

  const moveStage = async (stage: string) => {
    if (!ws || stage === ws.pursuit.stage) return;
    if (stage === 'submitted') { openSubmitGate(); return; }
    if ((stage === 'lost' || stage === 'archived') && !window.confirm(stage === 'lost' ? 'Mark this pursuit as lost?' : 'Archive this pursuit? It leaves the active pipeline.')) return;
    setBusy('stage');
    try {
      await pipelineApi.updateStage(ws.pursuit.id, { stage });
      setWs({ ...ws, pursuit: { ...ws.pursuit, stage } });
      toast.success(`Moved to ${STAGE_LABEL[stage] || stage}`);
      load(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || "Couldn't change the stage");
    } finally {
      setBusy(null);
    }
  };

  const runAnalyze = async () => {
    if (!ws) return;
    setBusy('analyze');
    try {
      const r = await pipelineApi.analyze(ws.pursuit.id);
      if (r.data?.data?.alreadyRunning) toast(r.data.data.message || 'Analysis is already running for this pursuit.');
      else toast.success('Analysis started. It takes a few minutes; this page updates when it lands.', { duration: 6000 });
      setTimeout(() => load(true), 15000);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || "Couldn't start the analysis");
    } finally {
      setBusy(null);
    }
  };

  const fetchDocuments = async () => {
    if (!ws) return;
    setBusy('docs');
    try {
      const r = await pipelineApi.fetchWorkspaceDocuments(ws.pursuit.id);
      const d = r.data.data;
      const from = sourceSiteName(ws.opportunity.source);
      if (d.saved === 0 && d.already_saved === 0) toast(d.note || `${from} lists no attachments for this notice.`);
      else toast.success(d.saved ? `Fetching ${d.saved} attachment${d.saved === 1 ? '' : 's'} from ${from}` : 'Every attachment is already saved');
      load(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || "Couldn't fetch the attachments");
    } finally {
      setBusy(null);
    }
  };

  const toggleChecklist = async (itemId: string) => {
    if (!ws?.proposal) return;
    const before = ws;
    const checklist = ws.proposal.checklist.map((c) => (c.id === itemId ? { ...c, isCompleted: !c.isCompleted } : c));
    const completed = checklist.filter((c) => c.isCompleted).length;
    setWs({ ...ws, proposal: { ...ws.proposal, checklist, checklistProgress: { total: checklist.length, completed } } });
    try {
      await aiApi.toggleChecklistItem(itemId);
    } catch {
      setWs(before);
      toast.error("Couldn't update that item");
    }
  };

  // ---- Submit gate ----
  interface GateFinding { code: string; severity: 'blocker' | 'check' | 'pass'; title: string; detail: string | null; quote?: string | null; tab?: Tab }
  interface GateReport { ok: boolean; blockers: GateFinding[]; checks: GateFinding[]; passes: GateFinding[]; ranAt: string }
  // Who holds this work: USASpending awards from the same office. Its own
  // request so a slow third party never delays the page.
  const [awards, setAwards] = useState<{ loading: boolean; error: string | null; data: AwardHistory | null }>({ loading: true, error: null, data: null });
  const pursuitId = ws?.pursuit.id;
  useEffect(() => {
    if (!pursuitId) return;
    let cancelled = false;
    setAwards({ loading: true, error: null, data: null });
    pipelineApi.getAwardHistory(pursuitId)
      .then((r) => { if (!cancelled) setAwards({ loading: false, error: null, data: r.data.data }); })
      .catch((e: any) => { if (!cancelled) setAwards({ loading: false, error: e?.response?.data?.error?.message || 'USASpending.gov did not answer.', data: null }); });
    return () => { cancelled = true; };
  }, [pursuitId]);

  const [gate, setGate] = useState<{ open: boolean; loading: boolean; report: GateReport | null; method: string; recipient: string; sentAt: string; confirmation: string } | null>(null);
  const openSubmitGate = async () => {
    if (!ws) return;
    const poc = Array.isArray(ws.opportunity.pointOfContact) ? ws.opportunity.pointOfContact.find((p: any) => p?.email) : null;
    const local = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setGate({ open: true, loading: true, report: null, method: 'email', recipient: poc?.email || '', sentAt: local, confirmation: '' });
    try {
      const r = await pipelineApi.getSubmitGate(ws.pursuit.id);
      setGate((g) => (g ? { ...g, loading: false, report: r.data.data } : g));
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || "Couldn't run the checks");
      setGate(null);
    }
  };
  const confirmSubmit = async (force: boolean) => {
    if (!ws || !gate) return;
    setBusy('submit');
    try {
      await pipelineApi.submitPursuit(ws.pursuit.id, {
        force, method: gate.method, recipient: gate.recipient.trim() || null,
        sentAt: gate.sentAt ? new Date(gate.sentAt).toISOString() : null, confirmation: gate.confirmation.trim() || null,
      });
      toast.success('Recorded as submitted.');
      setGate(null);
      await load(true);
    } catch (e: any) {
      if (e?.response?.status === 409 && e.response.data?.data) setGate((g) => (g ? { ...g, report: e.response.data.data } : g));
      else toast.error(e?.response?.data?.error?.message || "Couldn't record the submission");
    } finally {
      setBusy(null);
    }
  };

  const setDebrief = async (value: string) => {
    if (!ws) return;
    try {
      await pipelineApi.setDebriefDate(ws.pursuit.id, value || null);
      await load(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || "Couldn't save the date");
    }
  };

  const retryDocument = async (documentId: string) => {
    if (!ws) return;
    setBusy(`retry-${documentId}`);
    try {
      await pipelineApi.retryDocument(ws.pursuit.id, documentId);
      toast('Retrying the download and extraction.');
      setTimeout(() => load(true), 4000);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || "Couldn't retry");
    } finally {
      setBusy(null);
    }
  };

  const markReviewed = async () => {
    if (!ws) return;
    setBusy('reviewed');
    try {
      await pipelineApi.markReviewed(ws.pursuit.id);
      await load(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || "Couldn't save that");
    } finally {
      setBusy(null);
    }
  };

  const setOutcome = async (outcome: 'won' | 'lost' | 'missed' | null) => {
    if (!ws) return;
    setBusy('outcome');
    try {
      const r = await pipelineApi.setOutcome(ws.pursuit.id, outcome);
      const moved = r.data?.data?.stageMovedTo;
      if (outcome === 'won') toast.success(moved ? 'Recorded as won and moved to Awarded.' : 'Recorded as won.');
      else if (outcome === 'lost') toast(moved ? 'Recorded as lost. The request to send is below.' : 'Recorded as lost.');
      else if (outcome === 'missed') toast(moved ? 'Recorded and archived.' : 'Recorded.');
      await load(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || "Couldn't record that");
    } finally {
      setBusy(null);
    }
  };

  const setOverride = async (proceed: boolean) => {
    if (!ws) return;
    let note: string | undefined;
    if (proceed) {
      const typed = window.prompt('Proceed despite the blockers. Why? (kept on the pursuit, optional)');
      if (typed === null) return;
      note = typed.trim() || undefined;
    }
    setBusy('override');
    try {
      await pipelineApi.setOverride(ws.pursuit.id, proceed, note);
      await load(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || "Couldn't save that");
    } finally {
      setBusy(null);
    }
  };

  const tickKeyDate = async (rowKey: string, done: boolean) => {
    if (!ws?.keyDates) return;
    const before = ws;
    const actions = ws.keyDates.actions.map((a) => (a.key === rowKey ? { ...a, done, doneAt: done ? new Date().toISOString() : null } : a));
    const nextDue = actions.find((a) => !a.done && a.deadline && (a.status === 'today' || a.status === 'upcoming')) || null;
    setWs({ ...ws, keyDates: { ...ws.keyDates, actions, nextDue } });
    try {
      await pipelineApi.tickKeyDate(ws.pursuit.id, rowKey, done);
      load(true);
    } catch {
      setWs(before);
      toast.error("Couldn't save that");
    }
  };

  // ---- Derived facts ----
  const derived = useMemo(() => {
    if (!ws) return null;
    const newer = ws.notices.filter((n) => n.id !== ws.opportunity.id && n.postedDate && ws.opportunity.postedDate && n.postedDate > ws.opportunity.postedDate);
    const latest = newer[newer.length - 1];
    const deadlineMoved = !!(ws.opportunity.originalDeadline && ws.opportunity.responseDeadline && ws.opportunity.originalDeadline !== ws.opportunity.responseDeadline);
    const unread = ws.notices.some((n) => n.newSinceLastOpen);
    const analyses = ws.activity.filter((a) => a.agentType === 'contract-scout');
    const lastAnalysis = analyses.find((a) => a.status === 'completed') || null;
    const analysisRunning = analyses.some((a) => a.status === 'processing' || a.status === 'queued');
    const firstDoc = ws.documents.items[0]?.createdAt || null;
    const stageIndex = STAGE_PATH.indexOf(ws.pursuit.stage as any);
    return { newer, latest, deadlineMoved, unread, lastAnalysis, analysisRunning, firstDoc, stageIndex };
  }, [ws]);

  // ---- States ----
  if (loading) {
    return (
      <div className="space-y-4">
        <SkeletonBlock className="h-40" />
        <SkeletonBlock className="h-10" />
        <div className="grid gap-4 md:grid-cols-3"><SkeletonBlock className="h-48" /><SkeletonBlock className="h-48" /><SkeletonBlock className="h-48" /></div>
      </div>
    );
  }
  if (error === 'not_found') {
    return (
      <EmptyState
        icon={FolderIcon}
        title="This opportunity is not in your pipeline"
        description="A workspace exists for every pursuit you add. Find the solicitation and add it, and the workspace opens here."
        actionLabel="Find opportunities"
        actionHref="/opportunities"
      />
    );
  }
  if (error || !ws || !derived) {
    return <ErrorState title="Couldn't load this pursuit" description={error || undefined} onRetry={() => load()} retrying={loading} />;
  }

  const { opportunity: opp, pursuit, eligibility, documents, proposal, activity } = ws;
  const site = sourceSiteName(opp.source);
  const due = dueIn(opp.responseDeadline, opp.deadlineTimeKnown, site);
  const dueText = fmtDeadline(opp.responseDeadline, opp.deadlineTimeKnown);
  const closed = !!opp.responseDeadline && new Date(opp.responseDeadline).getTime() < Date.now();
  const oppPath = `/opportunities/${opp.samNoticeId}`;
  const writerPath = `/proposal-writer?opportunityId=${opp.samNoticeId}`;
  const place = placeLabel(opp.placeOfPerformance, opp.popState);
  const agencyLine = [formatAgency(opp.agency), place].filter(Boolean).join(' · ');
  const matrixStats = proposal?.matrixStats;
  const tabCount: Partial<Record<Tab, string>> = {
    documents: documents.total ? String(documents.total) : undefined,
    requirements: matrixStats?.total ? `${matrixStats.addressed} / ${matrixStats.total}` : proposal?.checklistProgress.total ? `${proposal.checklistProgress.completed} / ${proposal.checklistProgress.total}` : undefined,
    proposal: proposal?.sections.length ? String(proposal.sections.length) : undefined,
  };
  // One list feeds both the Activity tab and its badge, so the count is the rows.
  const activityRows: Array<{ at: string; what: React.ReactNode; tag: React.ReactNode }> = [
    ...ws.notices.filter((n) => n.id !== opp.id && n.postedDate).map((n) => ({
      at: n.postedDate as string,
      what: <><strong className="font-semibold">Newer notice posted on SAM.gov.</strong>{n.responseDeadline ? ` Response date ${fmtDeadline(n.responseDeadline, opp.deadlineTimeKnown)}.` : ''}</>,
      tag: n.newSinceLastOpen ? <Pill tone="warning">New</Pill> : null,
    })),
    ...(ws.award?.postedDate ? [{
      at: ws.award.postedDate,
      what: <><strong className="font-semibold">Award posted on SAM.gov</strong>{ws.award.awardee ? ` to ${ws.award.awardee}` : ''}{ws.award.amount ? ` for ${fmtMoney(ws.award.amount)}` : ''}.</>,
      tag: <Pill>Awarded</Pill>,
    }] : []),
    ...activity.map((a) => ({
      at: a.createdAt,
      what: <><strong className="font-semibold">{AGENT_LABEL[a.agentType] || a.agentType}</strong> {a.status === 'completed' ? `completed${a.completedAt ? ` in ${Math.max(1, Math.round((new Date(a.completedAt).getTime() - new Date(a.createdAt).getTime()) / 60000))} min` : ''}.` : a.status === 'failed' ? `failed${a.error ? `: ${a.error.slice(0, 100)}` : '.'}` : 'running.'}</>,
      tag: a.status === 'failed' ? <Pill tone="danger">Failed</Pill> : a.status !== 'completed' ? <Pill tone="accent">Running</Pill> : null,
    })),
    ...ws.conversations.map((c) => ({
      at: c.updatedAt,
      what: <><strong className="font-semibold">Consultant</strong>: {c.title} ({c.messages} message{c.messages === 1 ? '' : 's'}).</>,
      tag: null,
    })),
    ...pursuit.stageHistory.map((h) => ({
      at: h.at,
      what: <>Moved from <strong className="font-semibold">{STAGE_LABEL[h.from] || h.from}</strong> to <strong className="font-semibold">{STAGE_LABEL[h.to] || h.to}</strong>.</>,
      tag: null,
    })),
    ...(derived.firstDoc ? [{ at: derived.firstDoc, what: <><strong className="font-semibold">{documents.total} attachment{documents.total === 1 ? '' : 's'} fetched</strong> from {site}.</>, tag: null }] : []),
    { at: pursuit.createdAt, what: <><strong className="font-semibold">Added to pipeline.</strong></>, tag: null },
  ].sort((a, b) => (a.at < b.at ? 1 : -1));
  tabCount.activity = String(activityRows.length);

  return (
    <div className="space-y-4">
      <nav aria-label="Breadcrumb" className="text-label text-content-muted">
        <Link to="/pipeline?view=list" className="text-accent hover:text-accent-hover">Pursuits</Link>
        <span aria-hidden="true" className="mx-2">›</span>
        <span className="text-content">{opp.title}</span>
      </nav>

      {/* ---------- Header ---------- */}
      <Card className="p-5 sm:p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
          <div className="min-w-0 flex-1 space-y-2.5">
            <div className="flex flex-wrap items-center gap-2">
              {opp.type && <Pill tone="accent">{opp.type}</Pill>}
              {derived.unread && derived.latest && (
                <Pill tone="warning"><ExclamationTriangleIcon className="h-3 w-3" /> New notice posted {fmtDate(derived.latest.postedDate)}</Pill>
              )}
              {ws.award && <Pill>Awarded{ws.award.date ? ` ${fmtDate(ws.award.date)}` : ''}</Pill>}
              {closed && !ws.award && <Pill>Closed</Pill>}
            </div>
            <h1 className="text-title-lg text-content">{opp.title}</h1>
            {agencyLine && <p className="text-body text-content-muted">{agencyLine}</p>}

            {/* The one thing to do next, derived server-side; one button does it. */}
            {(() => {
              const step = ws.nextStep;
              // Dates are rendered here so they land in the reader's zone.
              const title = step.key === 'submit' ? `Submit by ${dueText}.` : step.key === 'deadline_passed' ? `The response date passed on ${dueText}.` : step.title;
              const a = step.action;
              const btn = 'inline-flex min-h-[40px] items-center gap-2 rounded-token px-3.5 text-label font-medium whitespace-nowrap';
              const primary = `${btn} bg-accent text-accent-on hover:bg-accent-hover disabled:opacity-50`;
              const secondary = `${btn} border border-border-strong bg-surface text-content hover:bg-surface-sunk disabled:opacity-50`;
              let control: React.ReactNode = null;
              if (a.type === 'fetch_documents') control = <button type="button" onClick={fetchDocuments} disabled={busy === 'docs'} className={primary}><ArrowPathIcon className={`h-4 w-4 ${busy === 'docs' ? 'animate-spin' : ''}`} /> Fetch attachments</button>;
              else if (a.type === 'analyze') control = <button type="button" onClick={runAnalyze} disabled={busy === 'analyze'} className={primary}><SparklesIcon className="h-4 w-4" /> {step.key === 'reanalyze' ? 'Re-run analysis · 1 credit' : 'Analyze · 1 credit'}</button>;
              else if (a.type === 'open_notice') control = (
                <span className="flex flex-wrap gap-2">
                  <a href="#amendments" className={primary}>See what changed</a>
                  <button type="button" onClick={markReviewed} disabled={busy === 'reviewed'} className={secondary}><CheckCircleIcon className="h-4 w-4" /> Mark reviewed</button>
                </span>
              );
              else if (a.type === 'open_award') control = <Link to={`/opportunities/${a.samNoticeId}`} className={secondary}>Open the award notice <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /></Link>;
              else if (a.type === 'open_tab') control = <button type="button" onClick={() => setTab(a.tab)} className={primary}>{a.tab === 'documents' ? 'Open documents' : a.tab === 'requirements' ? 'Open requirements' : 'Open proposal'}</button>;
              else if (a.type === 'open_proposal') control = <Link to={writerPath} className={primary}><DocumentTextIcon className="h-4 w-4" /> Start proposal</Link>;
              else if (a.type === 'fix_eligibility') control = <Link to="/settings" className={primary}>Company profile</Link>;
              else if (a.type === 'submit') control = (
                <span className="flex flex-wrap gap-2">
                  {a.email && <a href={`mailto:${a.email}?subject=${encodeURIComponent(`Response: ${opp.solicitationNumber || opp.title}`)}`} className={secondary}>Email {a.email}</a>}
                  <button type="button" onClick={openSubmitGate} className={primary}>Mark submitted</button>
                </span>
              );
              else if (a.type === 'archive') control = <button type="button" onClick={() => moveStage('archived')} className={secondary}>Archive</button>;
              else if (a.type === 'tick_key_date') control = <button type="button" onClick={() => tickKeyDate(a.key, true)} className={primary}><CheckCircleIcon className="h-4 w-4" /> Done, tick it off</button>;
              return (
                <div className={`mt-3 flex flex-col gap-3 rounded-token-md border px-4 py-3 sm:flex-row sm:items-center ${step.blocking ? 'border-warning/40 bg-warning-sunk' : 'border-accent/30 bg-accent-sunk'}`}>
                  <div className="min-w-0 flex-1">
                    <Overline className={step.blocking ? 'text-warning' : 'text-accent'}>Next step</Overline>
                    <div className="text-body font-semibold text-content">{title}</div>
                    {step.detail && <div className="text-label text-content-muted">{step.detail}</div>}
                  </div>
                  {control}
                </div>
              );
            })()}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-label text-content-muted">
              {opp.solicitationNumber && <span className="font-semibold text-content">{opp.solicitationNumber}</span>}
              {opp.naics && <span>NAICS {opp.naics}</span>}
              {opp.psc && <span>PSC {opp.psc}</span>}
              {(opp.setAsideDescription || opp.setAside) && <span>{opp.setAsideDescription || opp.setAside}</span>}
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <div role="group" aria-label="Pipeline stage" className="flex overflow-x-auto rounded-token border border-border text-caption font-semibold">
                {STAGE_PATH.map((s, i) => {
                  const state = derived.stageIndex < 0 ? 'off' : i < derived.stageIndex ? 'done' : i === derived.stageIndex ? 'cur' : 'off';
                  return (
                    <button
                      key={s}
                      type="button"
                      disabled={busy === 'stage'}
                      aria-current={state === 'cur' ? 'step' : undefined}
                      onClick={() => moveStage(s)}
                      className={`min-h-[36px] whitespace-nowrap px-3 border-r border-border last:border-r-0 ${
                        state === 'cur' ? 'bg-accent text-accent-on' : state === 'done' ? 'bg-accent-sunk text-accent' : 'bg-surface text-content-subtle hover:text-content'
                      }`}
                    >
                      {STAGE_LABEL[s]}
                    </button>
                  );
                })}
              </div>
              {pursuit.submission && (
                <span className="text-label text-content-muted">
                  Submitted {fmtDate(pursuit.submission.sentAt, true)}{pursuit.submission.method !== 'other' ? ` by ${pursuit.submission.method}` : ''}{pursuit.submission.recipient ? ` to ${pursuit.submission.recipient}` : ''}{pursuit.submission.gate.forced ? ' · with blockers overridden' : ''}
                </span>
              )}
              {(pursuit.stage === 'lost' || pursuit.stage === 'archived') ? (
                <Pill>{STAGE_LABEL[pursuit.stage]}</Pill>
              ) : (
                <div className="flex gap-2 text-label">
                  <button type="button" onClick={() => moveStage('lost')} className="text-content-muted hover:text-danger">Mark lost</button>
                  <span className="text-content-subtle">·</span>
                  <button type="button" onClick={() => moveStage('archived')} className="text-content-muted hover:text-content">Archive</button>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3 lg:items-end lg:text-right">
            <div>
              <Overline>{closed ? 'Response date' : 'Due in'}</Overline>
              <div className={`text-stat tabular-nums ${due.tone}`}>{due.big}</div>
              <div className="text-label text-content-muted">{due.small}</div>
              {derived.deadlineMoved && (
                <div className="text-caption text-warning">Moved from {fmtDate(opp.originalDeadline)} by a later notice</div>
              )}
              {ws.keyDates?.nextDue && !closed && (
                <div className="text-caption font-semibold text-warning">
                  {KIND_LABEL[ws.keyDates.nextDue.kind] || 'Deadline'} {ws.keyDates.nextDue.daysRemaining === 0 ? 'today' : ws.keyDates.nextDue.daysRemaining === 1 ? 'tomorrow' : `in ${ws.keyDates.nextDue.daysRemaining} days`}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 lg:justify-end">
              <Link to={oppPath} className="inline-flex min-h-[40px] items-center gap-2 rounded-token border border-border-strong bg-surface px-3.5 text-label font-medium text-content hover:bg-surface-sunk">
                <ChatBubbleLeftRightIcon className="h-4 w-4" /> Ask the consultant
              </Link>
              <Link to={writerPath} className="inline-flex min-h-[40px] items-center gap-2 rounded-token bg-accent px-3.5 text-label font-medium text-accent-on hover:bg-accent-hover">
                <DocumentTextIcon className="h-4 w-4" /> {proposal ? 'Open proposal' : 'Start proposal'}
              </Link>
            </div>
          </div>
        </div>
      </Card>

      {/* ---------- Award banner ---------- */}
      {ws.award && (
        <div className="flex flex-wrap items-center gap-3 rounded-token-md border border-border bg-surface-sunk px-4 py-3">
          <CheckCircleIcon className="h-5 w-5 flex-none text-content-muted" />
          <div className="min-w-0 flex-1 text-body text-content">
            <strong className="font-semibold">SAM.gov posted an award for this solicitation{ws.award.date ? ` on ${fmtDate(ws.award.date)}` : ws.award.postedDate ? ` on ${fmtDate(ws.award.postedDate)}` : ''}.</strong>{' '}
            {ws.award.awardee ? <>Awarded to <span className="font-medium">{ws.award.awardee}</span>{ws.award.amount ? ` for ${fmtMoney(ws.award.amount)}` : ''}{ws.award.number ? ` (${ws.award.number})` : ''}.</> : 'The notice names no awardee.'}
          </div>
          {pursuit.outcome ? (
            <span className="flex items-center gap-2 text-label">
              <Pill tone={pursuit.outcome.kind === 'won' ? 'success' : 'muted'}>{pursuit.outcome.kind === 'won' ? 'That was us' : pursuit.outcome.kind === 'lost' ? 'We lost' : 'We did not bid'}</Pill>
              <button type="button" onClick={() => setOutcome(null)} disabled={busy === 'outcome'} className="text-content-muted underline hover:text-content">undo</button>
            </span>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setOutcome('won')} disabled={busy === 'outcome'} className="inline-flex min-h-[34px] items-center rounded-token border border-border-strong bg-surface px-3 text-caption font-semibold text-content hover:bg-surface-sunk disabled:opacity-50">That is us</button>
              {pursuit.stage === 'submitted'
                ? <button type="button" onClick={() => setOutcome('lost')} disabled={busy === 'outcome'} className="inline-flex min-h-[34px] items-center rounded-token bg-accent px-3 text-caption font-semibold text-accent-on hover:bg-accent-hover disabled:opacity-50">We lost to them</button>
                : <button type="button" onClick={() => setOutcome('missed')} disabled={busy === 'outcome'} className="inline-flex min-h-[34px] items-center rounded-token bg-accent px-3 text-caption font-semibold text-accent-on hover:bg-accent-hover disabled:opacity-50">We did not bid · archive</button>}
              <Link to={`/opportunities/${ws.award.samNoticeId}`} className="inline-flex min-h-[34px] items-center gap-1 rounded-token px-2 text-caption font-semibold text-accent hover:text-accent-hover">
                Notice <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}
        </div>
      )}

      {/* ---------- After a loss: the ask, with the clock ---------- */}
      {pursuit.outcome?.kind === 'lost' && pursuit.outcome.askBasis && (() => {
        const ask = pursuit.outcome!.askBasis!;
        const poc = Array.isArray(opp.pointOfContact) ? opp.pointOfContact.find((p: any) => p?.email) : null;
        const subject = `Request for ${ask.rule === 'far_15_506' ? 'post-award debriefing' : 'brief explanation of basis for award'}: ${opp.solicitationNumber || opp.title}`;
        const body = `Dear ${poc?.fullName || 'Contracting Officer'},\n\n${opp.solicitationNumber ? `Regarding solicitation ${opp.solicitationNumber}` : `Regarding "${opp.title}"`}${ws.award?.date ? `, awarded ${fmtDate(ws.award.date)}` : ''}${ws.award?.awardee ? ` to ${ws.award.awardee}` : ''}:\n\n${ask.request}\n\nWe respectfully request this in writing at your earliest convenience.\n\nThank you,\n`;
        return (
          <Card className="border-warning/40 bg-warning-sunk p-5 space-y-2">
            <Overline className="text-warning">After a loss: the ask</Overline>
            <p className="text-body font-semibold text-content">{ask.title}</p>
            <p className="text-body text-content">{ask.request}</p>
            <p className="text-label text-content-muted">{ask.clock}</p>
            <div className="grid gap-2 border-t border-warning/30 pt-3 sm:grid-cols-[auto_1fr] sm:items-center">
              <label htmlFor="debrief-at" className="text-label font-medium text-content">Debriefing held on</label>
              <input id="debrief-at" type="date" defaultValue={pursuit.outcome!.debriefAt || ''} onBlur={(e) => { if (e.target.value !== (pursuit.outcome!.debriefAt || '')) setDebrief(e.target.value); }} className="input max-w-[220px]" />
            </div>
            {(pursuit.outcome!.clocks?.length ?? 0) > 0 && (
              <ul className="space-y-1 text-label">
                {pursuit.outcome!.clocks!.map((c) => {
                  const d = daysUntilDate(c.date);
                  return (
                    <li key={c.key} className="flex flex-wrap items-baseline gap-x-2">
                      <span className={`font-semibold tabular-nums ${d < 0 ? 'text-content-muted' : d <= 2 ? 'text-danger' : 'text-content'}`}>{fmtDeadline(c.date + 'T00:00:00Z', false)}{d >= 0 ? ` · ${d === 0 ? 'today' : d === 1 ? 'tomorrow' : `${d} days`}` : ' · passed'}</span>
                      <span className="text-content">{c.label}</span>
                      <span className="text-caption text-content-muted">{c.basis}</span>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              {poc?.email && <a href={`mailto:${poc.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`} className="inline-flex min-h-[36px] items-center gap-2 rounded-token bg-accent px-3 text-label font-medium text-accent-on hover:bg-accent-hover">Open the email to {poc.fullName || poc.email}</a>}
              <span className="text-caption text-content-subtle">{ask.evidence.length ? `Based on: ${ask.evidence.join(', ')}.` : 'The documents did not name the FAR part.'} Confirm timing with the contracting officer; this is not legal advice.</span>
            </div>
          </Card>
        );
      })()}

      {/* ---------- Amendments: what changed, and whether it has been reviewed ---------- */}
      {(derived.latest || (ws.keyDates && ws.keyDates.amendments.count > 0)) && !ws.award && (() => {
        const am = ws.keyDates?.amendments;
        const newerDocs = documents.items.filter((d) => d.sourceId && d.sourceId !== opp.samNoticeId);
        const unread = derived.unread;
        return (
          <Card id="amendments" className={`${unread ? 'border-warning/40' : ''}`}>
            <div className={`flex flex-wrap items-center justify-between gap-3 px-5 py-3 ${unread ? 'bg-warning-sunk' : 'border-b border-border'}`}>
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon className={`h-5 w-5 flex-none ${unread ? 'text-warning' : 'text-content-muted'}`} />
                <Overline className={unread ? 'text-warning' : ''}>
                  {am && am.count > 0 ? `${am.count} amendment${am.count === 1 ? '' : 's'}${am.designations.length ? ` · ${am.designations.join(', ')}` : ''}` : 'Newer notice'}
                  {derived.latest ? ` · latest posted ${fmtDate(derived.latest.postedDate)}` : ''}
                </Overline>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {unread ? (
                  <button type="button" onClick={markReviewed} disabled={busy === 'reviewed'} className="inline-flex min-h-[34px] items-center gap-1.5 rounded-token bg-accent px-3 text-caption font-semibold text-accent-on hover:bg-accent-hover disabled:opacity-50"><CheckCircleIcon className="h-4 w-4" /> Mark reviewed</button>
                ) : pursuit.amendmentReviewedAt ? (
                  <span className="text-caption text-content-muted">Reviewed {fmtDate(pursuit.amendmentReviewedAt)}</span>
                ) : null}
                {derived.latest && (
                  <Link to={`/opportunities/${derived.latest.samNoticeId}`} className="inline-flex min-h-[34px] items-center gap-1 rounded-token border border-border-strong bg-surface px-3 text-caption font-semibold text-content hover:bg-surface-sunk">
                    Open the notice <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            </div>
            <ul className="divide-y divide-border text-body">
              {derived.deadlineMoved && (
                <li className="flex items-start gap-3 px-5 py-3"><span className="text-warning">&#8635;</span><span><span className="font-semibold">Response date</span> {fmtDate(opp.originalDeadline, true)} &rarr; <span className="font-semibold">{dueText}</span></span></li>
              )}
              {am?.items.map((it, i) => (
                <li key={i} className="px-5 py-3 space-y-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-semibold text-content">{it.designation || 'Amendment'}</span>
                    <span className="text-caption text-content-muted">{it.filename}</span>
                    {it.textUnreadable ? <Pill tone="warning">Could not be read</Pill>
                      : it.acknowledgmentRequired === true ? <Pill tone="warning">Acknowledgment required</Pill>
                      : it.acknowledgmentRequired === false ? <Pill>No acknowledgment language</Pill> : null}
                  </div>
                  {it.changes.length > 0 ? (
                    <ul className="space-y-1 text-label text-content">
                      {it.changes.map((c, j) => (
                        <li key={j} className="flex gap-2">
                          <span className="text-content-subtle">&#8226;</span>
                          <span>{c.field ? <><span className="font-medium">{c.field}</span>{c.movedFrom || c.movedTo ? <>: {c.movedFrom || '…'} &rarr; <span className="font-medium">{c.movedTo || '…'}</span></> : null} <span className="text-content-muted">“{c.text.length > 160 ? `${c.text.slice(0, 160)}…` : c.text}”</span></> : <span className="text-content-muted">“{c.text.length > 200 ? `${c.text.slice(0, 200)}…` : c.text}”</span>}</span>
                        </li>
                      ))}
                    </ul>
                  ) : !it.textUnreadable ? <div className="text-label text-content-muted">No enumerated changes found in the text; read the file.</div> : null}
                </li>
              ))}
              {am && am.count > 0 && am.acknowledgmentRequired !== false && (
                <li className="flex items-start gap-3 px-5 py-3 text-label text-content"><CheckCircleIcon className="mt-0.5 h-4 w-4 flex-none text-accent" /><span>{proposal ? 'Acknowledgment is on your bid checklist.' : 'Acknowledgment will be added to the bid checklist when the proposal starts.'} Sign block 8 of each SF30, or list them in SF1449 block 17b.</span></li>
              )}
              <li className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-label text-content-muted">
                <span>{newerDocs.length ? `${newerDocs.length} attachment${newerDocs.length === 1 ? '' : 's'} from later notices saved${newerDocs.some((d) => d.extractionStatus !== 'ready' && d.status !== 'processed') ? ', some still extracting' : ''}.` : derived.latest ? 'No attachments from the later notice are saved yet.' : ''}</span>
                <span className="flex gap-3">
                  <button type="button" onClick={() => setTab('documents')} className="font-medium text-accent hover:text-accent-hover">Documents</button>
                  <button type="button" onClick={fetchDocuments} disabled={busy === 'docs'} className="font-medium text-accent hover:text-accent-hover disabled:opacity-50">Check {site} for files</button>
                </span>
              </li>
              {!am?.count && derived.latest && <li className="px-5 py-3 text-label text-content-muted">Anything drafted before {fmtDate(derived.latest.postedDate)} was drafted against the earlier version.</li>}
            </ul>
          </Card>
        );
      })()}


      {/* ---------- Newer notice banner ---------- */}
      {derived.latest && !ws.award && (
        <div className="flex flex-wrap items-center gap-3 rounded-token-md border border-warning/30 bg-warning-sunk px-4 py-3">
          <ExclamationTriangleIcon className="h-5 w-5 flex-none text-warning" />
          <div className="min-w-0 flex-1 text-body text-content">
            <strong className="font-semibold">SAM.gov posted a newer notice for this solicitation on {fmtDate(derived.latest.postedDate)}.</strong>{' '}
            {derived.deadlineMoved
              ? `The response date moved from ${fmtDate(opp.originalDeadline, true)} to ${dueText}.`
              : 'The response date did not change. Attachments may have.'}{' '}
            Anything drafted before then was drafted against the earlier version.
          </div>
          <Link to={`/opportunities/${derived.latest.samNoticeId}`} className="inline-flex min-h-[34px] items-center gap-1 rounded-token border border-border-strong bg-surface px-3 text-caption font-semibold text-content hover:bg-surface-sunk">
            Open the new notice <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}

      {/* ---------- Tabs ---------- */}
      <div role="tablist" aria-label="Workspace sections" className="flex gap-5 overflow-x-auto border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            type="button"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`flex min-h-[44px] items-center gap-2 whitespace-nowrap border-b-2 px-1 text-label ${
              tab === t.id ? 'border-accent font-semibold text-accent' : 'border-transparent text-content-muted hover:text-content'
            }`}
          >
            {t.label}
            {tabCount[t.id] && <span className="rounded-full bg-surface-sunk px-2 py-0.5 text-caption text-content-muted">{tabCount[t.id]}</span>}
          </button>
        ))}
      </div>

      {/* ---------- Overview ---------- */}
      {tab === 'overview' && (
        <section role="tabpanel" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="p-5 space-y-3">
              {(() => {
                const v = ws.verdict;
                const pill = v.kind === 'eligible' ? <Pill tone="success">Eligible</Pill>
                  : v.kind === 'blocked' ? <Pill tone="danger">{v.fatalCount} blocker{v.fatalCount === 1 ? '' : 's'}</Pill>
                  : v.kind === 'overridden' ? <Pill tone="warning">Proceeding anyway</Pill>
                  : v.kind === 'sole_source' ? <Pill tone="warning">Sole source</Pill>
                  : v.kind === 'already_awarded' ? <Pill>Awarded</Pill>
                  : <Pill>Nothing to bid</Pill>;
                const FIELD: Record<string, string> = { setAside: 'Set-aside', naics: 'NAICS', responseDeadline: 'Response date', place: 'Place of performance' };
                return (
                  <>
                    <div className="flex items-center justify-between"><Overline>Can we bid</Overline>{pill}</div>
                    <p className="text-body font-medium text-content">{v.headline}</p>
                    {v.changedSinceAdded.some((c) => c.field !== 'responseDeadline') && v.assessedNotice && (
                      <div className="rounded-token border border-warning/40 bg-warning-sunk px-3 py-2 text-label text-content">
                        <span className="font-semibold">Changed in the notice posted {fmtDate(v.assessedNotice.postedDate)}:</span>{' '}
                        {v.changedSinceAdded.filter((c) => c.field !== 'responseDeadline').map((c) => `${FIELD[c.field] || c.field} is now ${c.field === 'responseDeadline' ? fmtDate(c.to, true) : (c.to || 'not stated')}${c.from ? ` (was ${c.field === 'responseDeadline' ? fmtDate(c.from, true) : c.from})` : ''}`).join('; ')}.
                      </div>
                    )}
                    {v.respondDifferently && v.kind !== 'eligible' && v.kind !== 'blocked' && v.kind !== 'overridden' && (
                      <p className="text-label text-content-muted">{v.respondDifferently}</p>
                    )}
                    {v.issues.length === 0 ? (
                      v.gatesApply && <p className="text-body text-content-muted">Nothing in the notice rules you out, judged against your company profile.</p>
                    ) : (
                      <ul className="space-y-2.5 text-body">
                        {v.issues.map((i) => (
                          <li key={i.code} className="flex gap-2.5">
                            {i.weight === 'fatal'
                              ? <XCircleIcon className="mt-0.5 h-4 w-4 flex-none text-danger" />
                              : i.weight === 'warning'
                                ? <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-none text-warning" />
                                : <CheckCircleIcon className="mt-0.5 h-4 w-4 flex-none text-content-subtle" />}
                            <span className={i.weight === 'informational' ? 'text-content-muted' : ''}>
                              <span className="font-medium text-content">{i.title}.</span> <span className="text-content-muted">{i.detail}</span>
                              {i.actionPath && <> <Link to={i.actionPath} className="font-medium text-accent hover:text-accent-hover">{i.actionLabel}</Link></>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {v.kind === 'blocked' && (
                      <button type="button" onClick={() => setOverride(true)} disabled={busy === 'override'} className="text-label font-medium text-content-muted underline hover:text-content">Proceed anyway</button>
                    )}
                    {v.override && (
                      <div className="text-label text-content-muted">
                        Proceeding anyway since {fmtDate(v.override.at)}{v.override.note ? `: “${v.override.note}”` : ''}.{' '}
                        <button type="button" onClick={() => setOverride(false)} disabled={busy === 'override'} className="font-medium text-accent hover:text-accent-hover">Undo</button>
                      </div>
                    )}
                    <p className="text-caption text-content-subtle">
                      {v.assessedNotice
                        ? 'Checked against the current notice, not the one you added.'
                        : v.jurisdiction?.level === 'state'
                          ? `${v.jurisdiction.stateName} state work: checked against your company profile. SAM.gov, NAICS registration and federal set-asides do not apply; the state's own vendor registration does, and Clevor cannot see it.`
                          : 'Checked against your company profile, not your SAM.gov registration.'}
                    </p>
                  </>
                );
              })()}
            </Card>

            <Card className="p-5 space-y-3 md:order-last md:col-span-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Overline>Analysis</Overline>
                {ws.analysis && !derived.analysisRunning && (
                  <span className="flex items-center gap-2 text-label text-content-muted">
                    Ran {fmtDate(ws.analysis.completedAt || ws.analysis.createdAt)}
                    <button type="button" onClick={runAnalyze} disabled={busy === 'analyze'} className="font-medium text-accent hover:text-accent-hover disabled:opacity-50">Re-run · 1 credit</button>
                  </span>
                )}
              </div>
              {derived.analysisRunning ? (
                <p className="flex items-center gap-2 text-body text-content">
                  <ArrowPathIcon className="h-4 w-4 animate-spin text-accent" /> Analyzing the documents.
                  {analysisWaitStart && <span className="text-content-muted">{Math.max(0, Math.round((Date.now() - analysisWaitStart) / 60000))} min so far; usually three to eight.</span>}
                </p>
              ) : ws.analysis && ws.analysis.result && isAnalysisPayload(ws.analysis.result) ? (
                <>
                  {derived.latest && ws.analysis.createdAt < (derived.latest.postedDate || '') && (
                    <div className="rounded-token border border-warning/40 bg-warning-sunk px-3 py-2 text-label text-content">
                      This run read the version before the notice posted {fmtDate(derived.latest.postedDate)}. <button type="button" onClick={runAnalyze} disabled={busy === 'analyze'} className="font-medium text-accent underline">Re-run for the new notice</button>
                    </div>
                  )}
                  <AnalysisResult data={ws.analysis.result as AnalysisPayload} evidence={ws.analysis.keyTermEvidence} />
                  {ws.analysis.keyTermEvidence && (
                    <p className="text-caption text-content-subtle">
                      Key terms checked against {ws.analysis.docsRead} readable attachment{ws.analysis.docsRead === 1 ? '' : 's'}. “Inferred” means the analysis asserted it and the documents do not say it; verify before pricing on it.
                    </p>
                  )}
                </>
              ) : ws.analysis ? (
                <p className="text-body text-content-muted">The last run finished but returned no structured verdict. <button type="button" onClick={runAnalyze} disabled={busy === 'analyze'} className="font-medium text-accent underline">Run it again · 1 credit</button></p>
              ) : (
                <>
                  <p className="text-body text-content-muted">Not analyzed yet. Analyze reads the attachments and produces the fit summary, the requirements matrix and the bid checklist.</p>
                  {documents.total === 0 ? (
                    <button type="button" onClick={fetchDocuments} disabled={busy === 'docs'} className="inline-flex min-h-[36px] items-center gap-2 rounded-token border border-border-strong bg-surface px-3 text-label font-medium text-content hover:bg-surface-sunk disabled:opacity-50">
                      <ArrowPathIcon className={`h-4 w-4 ${busy === 'docs' ? 'animate-spin' : ''}`} /> Fetch the attachments first
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={runAnalyze}
                      disabled={busy === 'analyze' || documents.ready + documents.failed < documents.total || documents.readable === 0}
                      className="inline-flex min-h-[36px] items-center gap-2 rounded-token bg-accent px-3 text-label font-medium text-accent-on hover:bg-accent-hover disabled:opacity-50"
                    >
                      <SparklesIcon className="h-4 w-4" /> {documents.ready + documents.failed < documents.total ? 'Waiting for documents' : documents.readable === 0 ? 'No readable text to analyze' : 'Analyze · 1 credit'}
                    </button>
                  )}
                </>
              )}
            </Card>

            <Card className="border-accent/30 bg-accent-sunk p-5 space-y-3">
              <Overline className="text-accent">What is left</Overline>
              <ul className="space-y-2.5 text-body">
                <li className="flex justify-between gap-3"><span>Attachments with readable text</span>
                  <button type="button" onClick={() => setTab('documents')} className={`font-semibold tabular-nums ${documents.total && documents.readable < documents.ready ? 'text-warning' : 'text-accent'}`}>{documents.readable} / {documents.total}</button></li>
                <li className="flex justify-between gap-3"><span>Requirements not yet addressed</span>
                  <button type="button" onClick={() => setTab('requirements')} className="font-semibold tabular-nums text-accent">{matrixStats ? matrixStats.missing + matrixStats.partial : '–'}</button></li>
                <li className="flex justify-between gap-3"><span>Bid checklist</span>
                  <button type="button" onClick={() => setTab('requirements')} className={`font-semibold tabular-nums ${proposal && proposal.checklistProgress.completed === proposal.checklistProgress.total && proposal.checklistProgress.total > 0 ? 'text-success' : 'text-accent'}`}>
                    {proposal ? `${proposal.checklistProgress.completed} / ${proposal.checklistProgress.total}` : '–'}
                  </button></li>
                <li className="flex justify-between gap-3"><span>Proposal sections drafted</span>
                  <button type="button" onClick={() => setTab('proposal')} className="font-semibold tabular-nums text-accent">{proposal?.sections.length ?? 0}</button></li>
              </ul>
              {opp.pointOfContact && Array.isArray(opp.pointOfContact) && opp.pointOfContact[0]?.email && (
                <div className="border-t border-accent/20 pt-3 text-label text-content-muted">
                  Submit to <a href={`mailto:${opp.pointOfContact[0].email}`} className="text-accent">{opp.pointOfContact[0].fullName || opp.pointOfContact[0].email}</a>
                  {opp.responseDeadline ? ` before ${dueText}` : ''}.
                </div>
              )}
            </Card>
          </div>

          {/* Before you bid: dated obligations read out of the attachments. */}
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3">
              <Overline>Before you bid</Overline>
              <span className="text-caption text-content-muted">
                {!ws.keyDates ? 'Nothing read yet' : ws.keyDates.docsRead === 0 ? `No readable text in ${ws.keyDates.docsTotal} attachment${ws.keyDates.docsTotal === 1 ? '' : 's'}` : `Read from ${ws.keyDates.docsRead} of ${ws.keyDates.docsTotal} attachment${ws.keyDates.docsTotal === 1 ? '' : 's'}`}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-3 border-b border-border px-5 py-3">
              {[
                ['Posted', fmtDate(opp.postedDate), ''],
                ['Newer notice', derived.latest ? fmtDate(derived.latest.postedDate) : '', 'text-warning'],
                [closed ? 'Was due' : 'Due', dueText, closed ? '' : due.tone],
                ['Awarded', ws.award ? fmtDate(ws.award.date || ws.award.postedDate) : '', ''],
              ].filter(([, v]) => v).map(([k, v, tone]) => (
                <div key={k as string}>
                  <div className="text-caption text-content-muted">{k}</div>
                  <div className={`text-label font-semibold ${tone}`}>{v}</div>
                </div>
              ))}
            </div>
            {!ws.keyDates ? (
              <p className="px-5 py-4 text-body text-content-muted">
                {documents.total === 0 ? 'Fetch the attachments and the site visit, RSVP, question cutoff and registration dates are read out of them.' : 'Waiting for the attachments to extract.'}
              </p>
            ) : ws.keyDates.actions.length === 0 && ws.keyDates.conflicts.length === 0 ? (
              <p className="px-5 py-4 text-body text-content-muted">
                {ws.keyDates.docsRead === 0
                  ? 'The attachments have no readable text (usually scans). Open them yourself for site visits, question cutoffs and registration.'
                  : `No site visit, pre-proposal conference, question cutoff or registration found in the ${ws.keyDates.docsRead} attachment${ws.keyDates.docsRead === 1 ? '' : 's'} read. Worth a skim of the documents regardless.`}
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {ws.keyDates.actions.map((a) => {
                  const tone = a.done ? 'text-content-subtle' : a.status === 'past' ? 'text-content-muted' : a.status === 'today' || (a.daysRemaining !== null && a.daysRemaining <= 2) ? 'text-danger' : a.status === 'upcoming' && (a.daysRemaining ?? 99) <= 7 ? 'text-warning' : 'text-content';
                  const when = !a.deadline ? 'No date stated' : a.status === 'past' ? `Was ${fmtDeadline(a.deadline, false)}` : a.status === 'today' ? 'Today' : a.daysRemaining === 1 ? 'Tomorrow' : `${fmtDeadline(a.deadline, false)} · ${a.daysRemaining} days`;
                  return (
                    <li key={a.key} className={`flex items-start gap-3 px-5 py-3 ${a.done ? 'opacity-70' : ''}`}>
                      <input id={`kd-${a.key}`} type="checkbox" checked={a.done} onChange={() => tickKeyDate(a.key, !a.done)} className="mt-1 h-4 w-4 flex-none rounded border-border-strong text-accent focus:ring-accent" />
                      <label htmlFor={`kd-${a.key}`} className="min-w-0 flex-1 cursor-pointer">
                        <div className="flex flex-wrap items-baseline gap-x-3">
                          <span className={`text-label font-semibold tabular-nums ${tone}`}>{when}</span>
                          <span className="text-caption text-content-muted">{KIND_LABEL[a.kind] || a.kind}{a.deadlineSource === 'amended' ? ' · moved by an amendment' : a.deadlineSource === 'computed' || a.deadlineSource === 'in_passage' ? ' · date inferred' : ''}</span>
                        </div>
                        <div className={`text-body ${a.done ? 'line-through text-content-muted' : 'text-content'}`}>{a.requirement}</div>
                        {a.quote && <div className="mt-0.5 text-caption text-content-muted">“{a.quote.length > 200 ? `${a.quote.slice(0, 200)}…` : a.quote}” <span className="whitespace-nowrap font-medium">· {a.source}</span></div>}
                      </label>
                    </li>
                  );
                })}
                {ws.keyDates.conflicts.map((c, i) => (
                  <li key={`c-${i}`} className={`flex items-start gap-3 px-5 py-3 ${c.kind === 'conflict' ? 'bg-danger-sunk/60' : 'bg-success-sunk/50'}`}>
                    {c.kind === 'conflict' ? <XCircleIcon className="mt-0.5 h-4 w-4 flex-none text-danger" /> : <CheckCircleIcon className="mt-0.5 h-4 w-4 flex-none text-success" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-body text-content">
                        {c.kind === 'conflict'
                          ? <><span className="font-semibold">{c.filename === 'amendment change text' ? 'The latest amendment moves the close date to' : `${c.filename} says the response is due`} {fmtDeadline(c.stated_in_document + 'T00:00:00Z', false)}; SAM.gov's record says {dueText}.</span> {c.days_apart} days apart. Read the amendment before trusting either.</>
                          : <><span className="font-semibold">SAM.gov's deadline is the later one.</span> {c.filename} says {fmtDeadline(c.stated_in_document + 'T00:00:00Z', false)}; a later notice extended it to {dueText}.</>}
                      </div>
                      <div className="mt-0.5 text-caption text-content-muted">“{c.quote.length > 200 ? `${c.quote.slice(0, 200)}…` : c.quote}”</div>
                    </div>
                  </li>
                ))}
                {ws.keyDates.amendments.count > 0 && (
                  <li className="flex items-start gap-3 px-5 py-3 text-body text-content-muted">
                    <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-none text-warning" />
                    <span>
                      {ws.keyDates.amendments.count} amendment{ws.keyDates.amendments.count === 1 ? '' : 's'} in the document set{ws.keyDates.amendments.designations.length ? ` (${ws.keyDates.amendments.designations.join(', ')})` : ''}.{' '}
                      {ws.keyDates.amendments.acknowledgmentRequired === true ? 'Acknowledgment is required with your response.' : ws.keyDates.amendments.acknowledgmentRequired === null ? `${ws.keyDates.amendments.unreadable} could not be read; assume acknowledgment is required.` : 'No acknowledgment language found.'}
                    </span>
                  </li>
                )}
              </ul>
            )}
          </Card>

          {(opp.summary || opp.uiLink) && (
            <Card className="p-5 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Overline>What they are buying</Overline>
                {opp.uiLink && <a href={opp.uiLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-label font-medium text-accent hover:text-accent-hover">View on {site} <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /></a>}
              </div>
              {opp.summary && (
                <details className="group">
                  <summary className="cursor-pointer list-none text-body text-content">
                    <span className="line-clamp-2 group-open:line-clamp-none">{opp.summary}</span>
                    {opp.summary.length > 220 && <span className="text-label font-medium text-accent group-open:hidden"> more</span>}
                  </summary>
                </details>
              )}
            </Card>
          )}

          <Card id="awards" className="p-5 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Overline>Who holds this work now</Overline>
              {awards.data && awards.data.rows.length > 0 && (
                <a href={`https://www.usaspending.gov/search/`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-label font-medium text-accent hover:text-accent-hover">USASpending.gov <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /></a>
              )}
            </div>
            {awards.loading ? (
              <p className="text-body text-content-muted">Checking USASpending for prior awards…</p>
            ) : awards.error ? (
              <p className="text-body text-content-muted">{awards.error}</p>
            ) : !awards.data || !awards.data.resolved ? (
              <p className="text-body text-content-muted">
                {!awards.data?.query.naics && !awards.data?.query.psc
                  ? 'The notice carries no NAICS or PSC code, so there is nothing to look prior awards up by.'
                  : 'USASpending does not list this agency under a name we can match yet.'}
              </p>
            ) : awards.data.rows.length === 0 ? (
              <p className="text-body text-content-muted">
                USASpending shows no {awards.data.query.subtier ? titleCaseName(awards.data.query.subtier.replace(/^DEPT OF /, 'Department of ')) : 'agency'} awards under {awards.data.query.naics ? `NAICS ${awards.data.query.naics}` : `PSC ${awards.data.query.psc}`}{awards.data.query.state ? ` in ${awards.data.query.state}` : ''} since {awards.data.query.since.slice(0, 4)}. This may be new work, or it was bought under a different code.
              </p>
            ) : (
              <>
                {awards.data.summary.currentHolder ? (
                  <p className="text-body text-content">
                    <strong className="font-semibold">{titleCaseName(awards.data.summary.currentHolder.recipient)}</strong> holds {awards.data.summary.currentHolder.awardId}
                    {awards.data.summary.currentHolder.sameOffice ? ' from this contracting office' : ''}, {money(awards.data.summary.currentHolder.amount)}, {monthYear(awards.data.summary.currentHolder.startDate)} to {monthYear(awards.data.summary.currentHolder.endDate)}.
                    {(() => {
                      const end = awards.data!.summary.currentHolder!.endDate;
                      if (!end) return null;
                      const days = Math.ceil((Date.UTC(+end.slice(0, 4), +end.slice(5, 7) - 1, +end.slice(8, 10)) - Date.now()) / 86400000);
                      if (days < 0) return null;
                      return <> That award ends in <strong className="font-semibold">{days} day{days === 1 ? '' : 's'}</strong>{days <= 180 ? ', so this notice is likely its recompete or a bridge' : ''}.</>;
                    })()}
                    {awards.data.summary.sameOffice > 1 && <> This office has obligated {money(awards.data.summary.obligated)} across {awards.data.summary.sameOffice} awards for this kind of work since {awards.data.query.since.slice(0, 4)}.</>}
                  </p>
                ) : (
                  <p className="text-body text-content">
                    No award is running today. {awards.data.summary.sameOffice > 0
                      ? <>This office obligated {money(awards.data.summary.obligated)} across {awards.data.summary.sameOffice} award{awards.data.summary.sameOffice === 1 ? '' : 's'} for this kind of work since {awards.data.query.since.slice(0, 4)}; the most recent are below.</>
                      : <>Nothing from this contracting office; the agency's nearby awards are below.</>}
                  </p>
                )}
                <ul className="divide-y divide-border rounded-token border border-border">
                  {awards.data.rows.slice(0, 6).map((r) => (
                    <li key={r.url + r.awardId} className="grid gap-x-4 gap-y-1 px-3 py-2.5 md:grid-cols-[minmax(0,1.5fr)_90px_150px] md:items-center">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <a href={r.url} target="_blank" rel="noopener noreferrer" className="truncate text-body font-medium text-content hover:text-accent">{titleCaseName(r.recipient)}</a>
                          {r.active && <Pill tone="success">Active</Pill>}
                          {r.sameOffice && <Pill tone="accent">Same office</Pill>}
                          {r.scope === 'national' && <Pill>Outside {awards.data!.query.state}</Pill>}
                        </div>
                        <div className="truncate text-caption text-content-muted">{r.awardId}{r.description ? ` · ${r.description.toLowerCase().replace(/(^|\. )([a-z])/g, (m, p, c) => p + c.toUpperCase())}` : ''}</div>
                      </div>
                      <div className="text-label font-semibold tabular-nums text-content md:text-right">{money(r.amount)}</div>
                      <div className="text-caption tabular-nums text-content-muted md:text-right">{monthYear(r.startDate)}{r.endDate ? ` – ${monthYear(r.endDate)}` : ''}</div>
                    </li>
                  ))}
                </ul>
                <p className="text-caption text-content-subtle">
                  Contract awards by {awards.data.query.subtier ? titleCaseName(awards.data.query.subtier.replace(/^DEPT OF /, 'Department of ')) : titleCaseName(awards.data.query.toptier || '')}{awards.data.query.naics ? `, NAICS ${awards.data.query.naics}` : ''}{awards.data.query.psc ? ` or PSC ${awards.data.query.psc}` : ''}{awards.data.query.state ? `, performed in ${awards.data.query.state}` : ''}, since {awards.data.query.since.slice(0, 4)}. “Same office” means the award number starts with {awards.data.query.officeCode || 'this office’s code'}. Amounts are obligations to date, not ceilings.
                </p>
              </>
            )}
          </Card>

          <Card className="p-5 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Overline>Ask about this solicitation</Overline>
              {ws.conversations.length > 0 && <Link to={oppPath} className="text-label font-medium text-accent hover:text-accent-hover">{ws.conversations.length} earlier conversation{ws.conversations.length === 1 ? '' : 's'}</Link>}
            </div>
            <p className="text-body text-content-muted">
              {documents.ready > 0
                ? `Answers come from the ${documents.ready} attachment${documents.ready === 1 ? '' : 's'} and your profile.`
                : 'Fetch the attachments first so answers come from the documents rather than the notice summary.'}
            </p>
            <Link to={oppPath} className="inline-flex min-h-[40px] items-center gap-2 rounded-token border border-border-strong bg-surface px-3.5 text-label font-medium text-content hover:bg-surface-sunk">
              <ChatBubbleLeftRightIcon className="h-4 w-4" /> Open the consultant
            </Link>
          </Card>
        </section>
      )}

      {/* ---------- Documents ---------- */}
      {tab === 'documents' && (
        <section role="tabpanel" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-label text-content-muted">
              {documents.total === 0
                ? 'Nothing fetched yet.'
                : documents.ready + documents.failed >= documents.total
                  ? `${documents.readable} of ${documents.total} attachment${documents.total === 1 ? '' : 's'} have readable text${documents.readable < documents.ready ? `; ${documents.ready - documents.readable} ${documents.ready - documents.readable === 1 ? 'is a scan or did not decode' : 'are scans or did not decode'}` : ''}${documents.failed ? `; ${documents.failed} failed` : ''}. Answers and the requirements matrix are grounded in the readable ones.`
                  : `${documents.ready + documents.failed} of ${documents.total} settled. Extraction takes ten to sixty seconds a file.`}
            </p>
            <button type="button" onClick={fetchDocuments} disabled={busy === 'docs'} className="inline-flex min-h-[36px] items-center gap-2 rounded-token border border-border-strong bg-surface px-3 text-label font-medium text-content hover:bg-surface-sunk disabled:opacity-50">
              <ArrowPathIcon className={`h-4 w-4 ${busy === 'docs' ? 'animate-spin' : ''}`} /> {documents.total === 0 ? `Fetch attachments from ${site}` : `Check ${site} for new files`}
            </button>
          </div>
          {documents.total === 0 ? (
            <Card><EmptyState icon={FolderIcon} title="No attachments saved for this pursuit" description="Fetch them once and every answer, requirement and proposal section is grounded in the actual solicitation." actionLabel="Fetch attachments" onAction={fetchDocuments} compact /></Card>
          ) : (
            <Card className="divide-y divide-border">
              {documents.items.map((d) => {
                const ready = d.extractionStatus === 'ready' || d.status === 'processed';
                const failed = d.extractionStatus === 'error' || d.status === 'error';
                const hollow = ready && d.readability && d.readability !== 'ok';
                const noticeIdx = ws.notices.findIndex((n) => n.samNoticeId === d.sourceId);
                const noticeLabel = noticeIdx <= 0 ? 'Base' : `Notice ${noticeIdx + 1}`;
                const fromNewer = noticeIdx > 0 && d.sourceId !== opp.samNoticeId;
                return (
                  <div key={d.id} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${fromNewer ? 'bg-warning-sunk/60' : ''}`}>
                    <DocumentTextIcon className={`h-5 w-5 flex-none ${fromNewer ? 'text-warning' : 'text-content-muted'}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2"><Pill tone={fromNewer ? 'warning' : 'muted'}>{noticeLabel}</Pill><span className="truncate text-body font-medium text-content">{d.filename}</span></div>
                      <div className="text-caption text-content-muted">
                        Fetched {fmtDate(d.createdAt)}{d.size ? ` · ${fmtSize(d.size)}` : ''} ·{' '}
                        {ready && !hollow ? 'Text ready'
                          : hollow ? <span className="text-warning">{d.readability === 'hollow' ? 'No readable text (a scan); open and read it yourself' : d.readability === 'undecodable' ? 'Text did not decode (custom font); open and read it yourself' : 'File type not readable here; open it yourself'}</span>
                          : failed ? <span className="text-danger">Extraction failed{d.extractionError ? `: ${d.extractionError.slice(0, 80)}` : ''}</span>
                          : <span className="text-accent">Extracting…</span>}
                        {fromNewer && <span className="font-semibold text-warning"> · From a later notice</span>}
                      </div>
                    </div>
                    {failed && (
                      <button type="button" onClick={() => retryDocument(d.id)} disabled={busy === `retry-${d.id}`} className="inline-flex min-h-[34px] items-center gap-1 rounded-token border border-border-strong bg-surface px-3 text-caption font-semibold text-content hover:bg-surface-sunk disabled:opacity-50">
                        <ArrowPathIcon className={`h-3.5 w-3.5 ${busy === `retry-${d.id}` ? 'animate-spin' : ''}`} /> Retry
                      </button>
                    )}
                    <a href={d.url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[34px] items-center gap-1 rounded-token border border-border-strong bg-surface px-3 text-caption font-semibold text-content hover:bg-surface-sunk">
                      Open <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    </a>
                  </div>
                );
              })}
            </Card>
          )}
        </section>
      )}

      {/* ---------- Requirements ---------- */}
      {tab === 'requirements' && (
        <section role="tabpanel">
          {!proposal ? (
            <Card>
              <EmptyState
                icon={CheckCircleIcon}
                title="No requirements extracted yet"
                description={derived.lastAnalysis ? 'Start the proposal to build the bid checklist and the requirements matrix from the analysis.' : 'Run Analyze first. It reads the attachments and lists every requirement with where it came from.'}
                actionLabel={derived.lastAnalysis ? 'Start proposal' : undefined}
                actionHref={derived.lastAnalysis ? writerPath : undefined}
                compact
              />
            </Card>
          ) : (
            <div className="grid items-start gap-4 lg:grid-cols-2">
              <Card>
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <Overline>Bid checklist</Overline>
                  <Pill tone={proposal.checklistProgress.completed === proposal.checklistProgress.total && proposal.checklistProgress.total > 0 ? 'success' : 'muted'}>
                    {proposal.checklistProgress.completed} of {proposal.checklistProgress.total} done
                  </Pill>
                </div>
                {proposal.checklist.length === 0 ? (
                  <EmptyState title="No checklist yet" description="The Proposal Writer builds it from the solicitation." actionLabel="Open Proposal Writer" actionHref={writerPath} compact />
                ) : (
                  <ul className="divide-y divide-border">
                    {proposal.checklist.map((c) => (
                      <li key={c.id} className="flex items-start gap-3 px-4 py-3">
                        <input id={`ck-${c.id}`} type="checkbox" checked={c.isCompleted} onChange={() => toggleChecklist(c.id)} className="mt-1 h-4 w-4 flex-none rounded border-border-strong text-accent focus:ring-accent" />
                        <label htmlFor={`ck-${c.id}`} className="flex-1 cursor-pointer text-body text-content">
                          {c.title}
                          <div className="text-caption text-content-muted">
                            {CATEGORY_LABEL[c.category] || c.category}{c.linkedSection ? ` · feeds ${c.linkedSection}` : ''}{c.source === 'team' ? ' · from a sub or vendor' : ''}
                          </div>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                  <Overline>Requirements matrix</Overline>
                  {matrixStats && (
                    <div className="flex gap-1.5">
                      <Pill tone="success">{matrixStats.addressed} addressed</Pill>
                      {matrixStats.partial > 0 && <Pill tone="warning">{matrixStats.partial} partial</Pill>}
                      <Pill tone="danger">{matrixStats.missing} missing</Pill>
                    </div>
                  )}
                </div>
                {proposal.matrix.length === 0 ? (
                  <EmptyState title="No matrix yet" description="Analyze extracts it from the attachments." compact />
                ) : (
                  <>
                    <ul className="divide-y divide-border">
                      {(showAllMatrix ? proposal.matrix : proposal.matrix.slice(0, 12)).map((m) => (
                        <li key={m.id} className="flex items-start gap-3 px-4 py-3">
                          <Pill tone={m.status === 'addressed' ? 'success' : m.status === 'partial' ? 'warning' : m.status === 'n_a' ? 'muted' : 'danger'}>
                            {m.status === 'n_a' ? 'N/A' : m.status.charAt(0).toUpperCase() + m.status.slice(1)}
                          </Pill>
                          <div className="min-w-0 flex-1 text-body text-content">
                            <div className="line-clamp-2">{m.requirementText}</div>
                            <div className="text-caption text-content-muted">
                              {[m.sourceRef, CATEGORY_LABEL[m.category] || m.category, m.targetSection].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                    {proposal.matrix.length > 12 && (
                      <div className="border-t border-border px-4 py-3">
                        <button type="button" onClick={() => setShowAllMatrix((v) => !v)} className="text-label font-medium text-accent hover:text-accent-hover">
                          {showAllMatrix ? 'Show fewer' : `Show all ${proposal.matrix.length} requirements`}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </Card>
            </div>
          )}
        </section>
      )}

      {/* ---------- Pricing: the agency's own schedule, filled in place ---------- */}
      {tab === 'pricing' && (
        <section role="tabpanel" className="space-y-4">
          {!proposal ? (
            <Card>
              <EmptyState
                icon={DocumentTextIcon}
                title="Pricing lives on the proposal"
                description={documents.items.some((d) => /\.(xlsx|xlsm|xls|csv)$/i.test(d.filename) || /bid\s*schedule|pric(e|ing)\s*(work)?sheet|schedule\s*of\s*(prices|items|supplies)|clin/i.test(d.filename))
                  ? 'This solicitation ships a price schedule. Start the proposal and it is parsed here: unit prices typed in place, option periods checked, the agency\'s own workbook filled for download.'
                  : 'Start the proposal to price line items. If the solicitation carries a bid schedule, it is parsed and filled in place.'}
                actionLabel="Start proposal"
                actionHref={writerPath}
                compact
              />
            </Card>
          ) : (
            <BidSchedulePanel proposalId={proposal.id} />
          )}
        </section>
      )}

      {/* ---------- Proposal ---------- */}
      {tab === 'proposal' && (
        <section role="tabpanel" className="space-y-4">
          {!proposal ? (
            <Card>
              <EmptyState icon={DocumentTextIcon} title="No proposal started" description="The Proposal Writer drafts each section from the bid checklist, your past performance library and the solicitation's own documents." actionLabel="Start proposal" actionHref={writerPath} compact />
            </Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-label text-content-muted">
                  {proposal.sections.length === 0 ? 'No sections drafted yet.' : `${proposal.sections.length} section${proposal.sections.length === 1 ? '' : 's'} · ${proposal.sections.reduce((n, s) => n + (s.wordCount || 0), 0).toLocaleString()} words · last edited ${fmtDate(proposal.updatedAt)}`}
                  {derived.latest && proposal.updatedAt < (derived.latest.postedDate || '') && <span className="text-warning"> · Drafted before the newer notice</span>}
                </p>
                <Link to={writerPath} className="inline-flex min-h-[36px] items-center gap-2 rounded-token bg-accent px-3 text-label font-medium text-accent-on hover:bg-accent-hover">
                  <DocumentTextIcon className="h-4 w-4" /> Open in Proposal Writer
                </Link>
              </div>
              {proposal.sections.length > 0 && (
                <Card className="divide-y divide-border">
                  {proposal.sections.map((s) => (
                    <div key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                      <Pill tone="success">Drafted</Pill>
                      <div className="min-w-0 flex-1">
                        <div className="text-body font-medium text-content">{s.title}</div>
                        <div className="text-caption text-content-muted">{(s.wordCount || 0).toLocaleString()} words · v{s.version} · edited {fmtDate(s.updatedAt)}</div>
                      </div>
                      <Link to={writerPath} className="text-label font-medium text-accent hover:text-accent-hover">Open</Link>
                    </div>
                  ))}
                </Card>
              )}
            </>
          )}
        </section>
      )}

      {/* ---------- Activity ---------- */}
      {tab === 'activity' && (
        <section role="tabpanel">
          <Card className="divide-y divide-border">
            {activityRows
              .map((row, i) => (
                <div key={i} className="flex items-start gap-3 px-4 py-3">
                  <div className="w-20 flex-none text-label text-content-muted">{fmtDate(row.at)}</div>
                  <div className="min-w-0 flex-1 text-body text-content">{row.what}</div>
                  {row.tag}
                </div>
              ))}
          </Card>
        </section>
      )}

      {/* ---------- Submit gate ---------- */}
      {gate?.open && (
        <div role="dialog" aria-modal="true" aria-label="Move to Submitted" className="fixed inset-0 z-50 flex items-end justify-center bg-content/40 p-0 sm:items-center sm:p-6" onClick={() => !busy && setGate(null)}>
          <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-token-lg bg-surface p-5 shadow-e3 sm:rounded-token-lg sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-title-lg text-content">Move to Submitted?</div>
            <p className="mt-1 text-label text-content-muted">Clevor checked the response against the solicitation. Nothing here costs a credit.</p>
            {gate.loading ? (
              <p className="mt-4 flex items-center gap-2 text-body text-content"><ArrowPathIcon className="h-4 w-4 animate-spin text-accent" /> Running the checks</p>
            ) : gate.report && (
              <ul className="mt-4 space-y-2 text-body">
                {[...gate.report.blockers, ...gate.report.checks, ...gate.report.passes].map((f) => (
                  <li key={f.code} className={`flex items-start gap-2.5 rounded-token px-3 py-2 ${f.severity === 'blocker' ? 'bg-danger-sunk text-content' : f.severity === 'check' ? 'bg-warning-sunk text-content' : 'bg-success-sunk text-content'}`}>
                    {f.severity === 'blocker' ? <XCircleIcon className="mt-0.5 h-4 w-4 flex-none text-danger" /> : f.severity === 'check' ? <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-none text-warning" /> : <CheckCircleIcon className="mt-0.5 h-4 w-4 flex-none text-success" />}
                    <span className="min-w-0 flex-1">
                      <span className="font-medium">{f.title}</span>
                      {f.detail && <span className="text-content-muted"> {f.detail}</span>}
                      {f.quote && <span className="block text-caption text-content-muted">“{f.quote.slice(0, 160)}”</span>}
                      {f.tab && f.severity !== 'pass' && <button type="button" onClick={() => { setTab(f.tab as Tab); setGate(null); }} className="ml-1 text-label font-medium text-accent underline">{f.tab === 'overview' ? 'Overview' : f.tab.charAt(0).toUpperCase() + f.tab.slice(1)}</button>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-5 text-label font-semibold text-content">Record the submission</div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <select value={gate.method} onChange={(e) => setGate({ ...gate, method: e.target.value })} className="input" aria-label="How it was sent">
                <option value="email">By email</option><option value="portal">Through a portal</option><option value="hand">Hand delivered</option><option value="mail">By mail</option><option value="other">Other</option>
              </select>
              <input value={gate.recipient} onChange={(e) => setGate({ ...gate, recipient: e.target.value })} placeholder="Recipient (email, portal, office)" className="input" aria-label="Recipient" />
              <input type="datetime-local" value={gate.sentAt} onChange={(e) => setGate({ ...gate, sentAt: e.target.value })} className="input" aria-label="Sent at" />
              <input value={gate.confirmation} onChange={(e) => setGate({ ...gate, confirmation: e.target.value })} placeholder="Confirmation or receipt (optional)" className="input" aria-label="Confirmation" />
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <div>{gate.report && !gate.report.ok && <button type="button" onClick={() => confirmSubmit(true)} disabled={busy === 'submit'} className="text-label font-medium text-danger underline disabled:opacity-50">Submit anyway</button>}</div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setGate(null)} disabled={busy === 'submit'} className="inline-flex min-h-[40px] items-center rounded-token border border-border-strong bg-surface px-3.5 text-label font-medium text-content hover:bg-surface-sunk">Cancel</button>
                <button type="button" onClick={() => confirmSubmit(false)} disabled={busy === 'submit' || gate.loading || !gate.report?.ok} className="inline-flex min-h-[40px] items-center rounded-token bg-accent px-3.5 text-label font-medium text-accent-on hover:bg-accent-hover disabled:opacity-50">{busy === 'submit' ? 'Saving…' : 'Mark submitted'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PursuitWorkspace;
